/**
 * Dashboard de dados wearable no atendimento: range de datas, KPIs por métrica e gráfico de tendência.
 * Dados somente de Observations FHIR enviadas pelo paciente (sem Google Health API).
 */
import { Box, Skeleton, Stack, Typography, useTheme } from "@mui/material";
import dayjs, { type Dayjs } from "dayjs";
import "dayjs/locale/pt-br";
import { useEffect, useMemo, useState } from "react";

import { useMedicalCareContext } from "@/contexts/MedicalCareContext";
import { oids } from "@/configs/Settings";
import { GetObservation } from "@/services/fhir/observation/GetObservation";
import { useFhirData } from "@/utils/useFhirData";

import { scrollbarStyle } from "../scrollbarStyle";

import { type WearableRangeDays, normalizeWearableRangeDays } from "./WearableDateRangeChips";
import { WearableFilterBar } from "./WearableFilterBar";
import { WearableKpiCard } from "./WearableKpiCard";
import { WearableMetricDayDetail } from "./WearableMetricDayDetail";
import { WearableMetricDetailPanel } from "./WearableMetricDetailPanel";
import { WearableMetricLineChart } from "./WearableMetricLineChart";
import { WearableViewModeToggle } from "./WearableViewModeToggle";
import {
  buildWearableMetricSummaries,
  filterEntriesByDateRange,
  filterWearableObservationEntries,
  formatAggregationModeLabel,
  formatPeriodAggregationMeta,
  formatWearablePeriodLabel,
  getDefaultGlobalAggregationMode,
  getWearableKpiCountForMode,
  resolveSelectedMetricKey,
  resolveWearablePeriod,
  type FhirObservationEntry,
  type WearableAggregationMode,
  type WearableViewMode,
} from "./wearableMetricAggregation";

function WearableKpiGridSkeleton({ kpiCount }: { kpiCount: number }) {
  return (
    <Box
      sx={{
        display: "grid",
        width: "100%",
        gap: 1,
        gridTemplateColumns: {
          xs: "repeat(3, minmax(0, 1fr))",
          sm: "repeat(3, minmax(0, 1fr))",
          md: "repeat(5, minmax(0, 1fr))",
        },
      }}
    >
      {Array.from({ length: kpiCount }).map((_, index) => (
        <Skeleton
          key={`wearable-kpi-skeleton-${index}`}
          variant="rounded"
          animation="wave"
          height={80}
          sx={{ borderRadius: 2 }}
        />
      ))}
    </Box>
  );
}

interface DrillDownSnapshot {
  previousRangeDays: WearableRangeDays;
  previousPeriodEndDate: Dayjs;
}

export function WearableDataView() {
  const theme = useTheme();
  const { summaryData, loadingSummary, patient } = useMedicalCareContext();
  const today = useMemo(() => dayjs().startOf("day"), []);
  const [viewMode, setViewMode] = useState<WearableViewMode>("fitness");
  const [periodEndDate, setPeriodEndDate] = useState<Dayjs>(() => dayjs().startOf("day"));
  const [rangeDays, setRangeDays] = useState<WearableRangeDays>(1);
  const [globalAggregationMode, setGlobalAggregationMode] = useState<WearableAggregationMode>(
    () => getDefaultGlobalAggregationMode("fitness")
  );
  const [drillDownSnapshot, setDrillDownSnapshot] = useState<DrillDownSnapshot | null>(null);
  const [selectedMetricKey, setSelectedMetricKey] = useState<string | null>(null);
  const [rangeFetchLoading, setRangeFetchLoading] = useState(false);
  const [rangeEntries, setRangeEntries] = useState<FhirObservationEntry[]>([]);

  const kpiCount = getWearableKpiCountForMode(viewMode);

  const observationSummary = useFhirData.extractPatientSummaryResource(
    summaryData,
    "Observation"
  )?.[0];

  const patientIdentifier =
    patient?.identifier?.find((id: { system?: string; value?: string }) =>
      id.system?.includes(oids.cpf)
    )?.value || patient?.id;

  const patientPerformerReference = patientIdentifier
    ? `Patient/${oids.cpf}-${patientIdentifier}`
    : "";

  const patientSubjectReference = patientPerformerReference;

  const { periodStart, periodEnd } = useMemo(
    () => resolveWearablePeriod({ selectedDate: periodEndDate, rangeDays, today }),
    [periodEndDate, rangeDays, today]
  );

  const periodLabel = useMemo(
    () => formatWearablePeriodLabel(periodStart, periodEnd, rangeDays),
    [periodStart, periodEnd, rangeDays]
  );

  const periodStartKey = periodStart.format("YYYY-MM-DD");
  const periodEndKey = periodEnd.format("YYYY-MM-DD");

  const rawSummaryEntries = useMemo(
    () => (observationSummary?.entry ?? []) as FhirObservationEntry[],
    [observationSummary?.entry]
  );

  useEffect(() => {
    if (rangeDays === 1) return;

    if (!patientIdentifier) {
      setRangeEntries([]);
      return;
    }

    let cancelled = false;

    async function loadRangeObservations() {
      setRangeFetchLoading(true);
      try {
        const bundle = await GetObservation({
          patientId: patientIdentifier,
          count: 500,
          startDate: periodStartKey,
          endDate: periodEndKey,
        });
        if (cancelled) return;
        const entries = (bundle?.entry ?? []) as FhirObservationEntry[];
        setRangeEntries(entries);
      } catch {
        if (!cancelled) setRangeEntries([]);
      } finally {
        if (!cancelled) setRangeFetchLoading(false);
      }
    }

    loadRangeObservations();
    return () => {
      cancelled = true;
    };
  }, [rangeDays, patientIdentifier, periodStartKey, periodEndKey]);

  const filterOptions = useMemo(
    () => ({
      mode: viewMode,
      patientPerformerReference,
      patientSubjectReference,
    }),
    [viewMode, patientPerformerReference, patientSubjectReference]
  );

  const observationEntries = useMemo(() => {
    const sourceEntries = rangeDays === 1 ? rawSummaryEntries : rangeEntries;
    const wearableEntries = filterWearableObservationEntries(sourceEntries, filterOptions);
    return filterEntriesByDateRange(wearableEntries, periodStart, periodEnd);
  }, [
    rangeDays,
    rawSummaryEntries,
    rangeEntries,
    filterOptions,
    periodStart,
    periodEnd,
  ]);

  const metricSummaries = useMemo(
    () =>
      buildWearableMetricSummaries(
        observationEntries,
        rangeDays,
        periodStart,
        periodEnd,
        theme,
        viewMode,
        globalAggregationMode
      ),
    [observationEntries, rangeDays, periodStart, periodEnd, theme, viewMode, globalAggregationMode]
  );

  const isLoading = rangeDays === 1 ? loadingSummary : rangeFetchLoading;
  const hasAnyMetricData = metricSummaries.some((summary) => summary.hasData);

  useEffect(() => {
    setSelectedMetricKey((current) => {
      const next = resolveSelectedMetricKey(metricSummaries, current);
      return next === current ? current : next;
    });
  }, [metricSummaries]);

  const selectedMetric = useMemo(
    () => metricSummaries.find((summary) => summary.loinc === selectedMetricKey) ?? null,
    [metricSummaries, selectedMetricKey]
  );

  const handleViewModeChange = (mode: WearableViewMode) => {
    setViewMode(mode);
    setSelectedMetricKey(null);
    setGlobalAggregationMode(getDefaultGlobalAggregationMode(mode));
  };

  const clearDrillDownSnapshot = () => {
    setDrillDownSnapshot(null);
  };

  const handleRangeChange = (days: WearableRangeDays) => {
    clearDrillDownSnapshot();
    setRangeDays(normalizeWearableRangeDays(days));
  };

  const handleChartDayClick = (dateKey: string) => {
    if (rangeDays <= 1) return;
    setDrillDownSnapshot({
      previousRangeDays: rangeDays,
      previousPeriodEndDate: periodEndDate,
    });
    setRangeDays(1);
    setPeriodEndDate(dayjs(dateKey).startOf("day"));
  };

  const handleRestorePeriod = () => {
    if (!drillDownSnapshot) return;
    setRangeDays(drillDownSnapshot.previousRangeDays);
    setPeriodEndDate(drillDownSnapshot.previousPeriodEndDate);
    setDrillDownSnapshot(null);
  };

  const handlePrevDay = () => {
    if (isLoading) return;
    clearDrillDownSnapshot();
    setPeriodEndDate((current) => current.subtract(1, "day").startOf("day"));
  };

  const handleNextDay = () => {
    if (isLoading) return;
    clearDrillDownSnapshot();
    setPeriodEndDate((current) => {
      const candidate = current.add(1, "day").startOf("day");
      return candidate.isAfter(today, "day") ? today : candidate;
    });
  };

  const handlePickDate = (picked: Dayjs) => {
    if (isLoading) return;
    clearDrillDownSnapshot();
    const clamped = picked.isAfter(today, "day") ? today : picked.startOf("day");
    setPeriodEndDate(clamped);
  };

  return (
    <Stack
      sx={{
        pt: 1.5,
        pr: 1.25,
        pb: 1.5,
        pl: 1,
        flex: 1,
        minHeight: 0,
        height: "100%",
        overflow: "hidden",
      }}
      spacing={1.25}
    >
      <WearableViewModeToggle
        mode={viewMode}
        onChange={handleViewModeChange}
        disabled={isLoading}
      />

      <WearableFilterBar
        rangeDays={rangeDays}
        onRangeChange={handleRangeChange}
        periodLabel={periodLabel}
        periodEnd={periodEnd}
        today={today}
        loading={isLoading}
        onPrevDay={handlePrevDay}
        onNextDay={handleNextDay}
        onPickDate={handlePickDate}
        aggregationMode={globalAggregationMode}
        onAggregationModeChange={setGlobalAggregationMode}
      />

      <Stack
        sx={{
          flex: 1,
          minHeight: 0,
          minWidth: 0,
          overflowY: "auto",
          overflowX: "hidden",
          gap: 2,
          pb: 2,
          WebkitOverflowScrolling: "touch",
          ...scrollbarStyle,
        }}
      >
        {isLoading ? (
          <>
            <WearableKpiGridSkeleton kpiCount={kpiCount} />
            <Skeleton variant="rounded" animation="wave" height={280} sx={{ borderRadius: 2 }} />
          </>
        ) : (
          <>
            <Box
              sx={{
                display: "grid",
                width: "100%",
                boxSizing: "border-box",
                gap: 1,
                gridTemplateColumns: {
                  xs: "repeat(3, minmax(0, 1fr))",
                  sm: "repeat(3, minmax(0, 1fr))",
                  md: "repeat(5, minmax(0, 1fr))",
                },
              }}
            >
              {metricSummaries.map((summary) => (
                <Box key={summary.loinc} sx={{ minWidth: 0 }}>
                  <WearableKpiCard
                    label={summary.label}
                    unit={summary.unit}
                    value={summary.aggregatedValue}
                    color={summary.color}
                    icon={summary.icon}
                    isSelected={selectedMetricKey === summary.loinc}
                    hasData={summary.hasData}
                    aggregationHint={
                      summary.hasData && rangeDays > 1
                        ? formatAggregationModeLabel(summary.activeAggregationMode)
                        : undefined
                    }
                    onClick={
                      summary.hasData
                        ? () => setSelectedMetricKey(summary.loinc)
                        : undefined
                    }
                  />
                </Box>
              ))}
            </Box>

            {viewMode === "sleep" && !hasAnyMetricData ? (
              <Box
                sx={{
                  py: 3,
                  px: 2,
                  borderRadius: 2,
                  border: "1px dashed",
                  borderColor: "divider",
                  textAlign: "center",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Nenhum dado de sono do FluxHealth neste período.
                </Typography>
              </Box>
            ) : null}

            {selectedMetric?.hasData ? (
              <WearableMetricDetailPanel
                visible
                color={selectedMetric.color}
                label={selectedMetric.label}
                meta={formatPeriodAggregationMeta(
                  selectedMetric.aggregatedValue,
                  selectedMetric.activeAggregationMode,
                  rangeDays,
                  periodLabel
                )}
              >
                {rangeDays === 1 ? (
                  <WearableMetricDayDetail
                    color={selectedMetric.color}
                    displayValue={selectedMetric.aggregatedValue}
                    unit={selectedMetric.unit}
                    dateLabel={periodLabel}
                    onRestorePeriod={
                      drillDownSnapshot ? handleRestorePeriod : undefined
                    }
                  />
                ) : (
                  <WearableMetricLineChart
                    color={selectedMetric.color}
                    unit={selectedMetric.unit}
                    dailyPoints={selectedMetric.dailyPoints}
                    rangeDays={rangeDays}
                    onDayClick={handleChartDayClick}
                  />
                )}
              </WearableMetricDetailPanel>
            ) : null}
          </>
        )}
      </Stack>
    </Stack>
  );
}
